import React from "react";
import { CustomerBaiscDetailsLayout } from "../../CustomerBasicDetailsLayout/CustomerBasicDetailsLayout";
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { StandadCard } from "../../../../../components/Cards";
import { Box } from "@mui/material";
import { PolicyTabCard } from "../../../../../components/PolicyTabCard/PolicyTabCard";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../../../redux/initialStoreState";
import { useDispatchWrapper } from "../../../../../hooks";
import { PageLoader } from "../../../../../components/PageLoader/PageLoader";
import { LoadState } from "../../../../../constants/enums";
import { Update } from "@mui/icons-material";
import { clearCustomerPolicyDetailsState } from "../../../../../redux/customerPolicyDetails/customerPolicyDetailsActions";

export const BusinessPolicyLayout: React.FC = () => {
  const { customerId, customerPolicyId } = useParams() as {
    customerId?: number;
    customerPolicyId?: number;
  };
  // The merged customer-policy-details fetch is dispatched by the CustomerInfo
  // screen via useCustomerPolicyDetails. The layout only observes that shared
  // slice for loading/error and the saved customer_policy_id used to enable the
  // sidebar tabs. It falls back to the route param so the tabs stay enabled
  // while viewing an existing policy (including direct navigation to non-basic
  // -details routes).
  const {
    customerPolicyId: mergedCustomerPolicyId,
    details: { loading, error },
  } = useSelector(
    (storeState: IStoreState) => storeState.customerPolicyDetails,
  );
  // Prefer the URL id as the source of truth. Only fall back to the merged
  // slice id when the URL has none (e.g. right after a new policy is saved in
  // this session). Preferring the slice id here caused a cross-customer leak
  // when switching between customers.
  const savedCustomerPolicyId = customerPolicyId ?? mergedCustomerPolicyId;
  const navigate = useNavigate();
  const dispatch = useDispatchWrapper();
  const location = useLocation();
  const currentPathname = location.pathname;
  const isMainTab = currentPathname.includes("basic-details");

  const handleStep = (step: string) => () => {
    const isAlreadyInSamePage = currentPathname.includes(step);
    if (!isAlreadyInSamePage) {
      navigate(
        `/business-policy/${customerId}/${step}/${savedCustomerPolicyId}?tab=0`,
      );
    }
  };

  const renderComponent = () => {
    if (isMainTab) {
      return <Outlet />;
    } else if (!isMainTab && !customerPolicyId) {
      return <Navigate to={`/customer-overview/${customerId}`} />;
    } else if (!isMainTab && savedCustomerPolicyId) {
      return <Outlet context={[customerId, savedCustomerPolicyId]} />;
    } else {
      console.log("__FALIED____");
      return null;
    }
  };

  // Clear the merged customer-policy-details slice whenever the customer or
  // policy in the URL CHANGES. React Router keeps this layout mounted while
  // only the :customerId/:customerPolicyId params change (same route pattern),
  // so the unmount cleanup below never fires when navigating A -> B -> A.
  // Without this reset, the previously opened customer's details stay in the
  // slice and leak into the next customer's form until the re-fetch resolves.
  // The first run is skipped so it doesn't wipe the child form's initial
  // in-flight fetch (effects run child-first, then parent).
  const hasMountedRef = React.useRef(false);
  React.useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    dispatch(clearCustomerPolicyDetailsState());
    dispatch({ type: "CLEAR_POLICY_STATE" });
  }, [customerId, customerPolicyId]);

  React.useEffect(() => {
    return () => {
      dispatch(clearCustomerPolicyDetailsState());
      dispatch({ type: "CLEAR_POLICY_STATE" });
    };
  }, []);

  return (
    <>
      <CustomerBaiscDetailsLayout
        customerId={customerId}
        fromPolicy
        policyId={customerPolicyId}
      >
          <StandadCard sx={{ mb: 1 }}>
            <Box display={"flex"} flexWrap={"wrap"}>
              <PolicyTabCard
                label="Basic Details"
                onClick={handleStep("basic-details")}
                isSelected={currentPathname.includes("basic-details")}
              />

              <PolicyTabCard
                label="Underwriting"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("underwriting")}
                isSelected={currentPathname.includes("underwriting")}
              />
              <PolicyTabCard
                label="Risk Locations & Coverage"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("coverage-tab")}
                isSelected={currentPathname.includes("coverage-tab")}
              />

              <PolicyTabCard
                label="Claims"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("claims-tab")}
                isSelected={currentPathname.includes("claims-tab")}
              />
              <PolicyTabCard
                label="Updates"
                icon={Update}
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("updates")}
                isSelected={currentPathname.includes("updates")}
              />
              <PolicyTabCard
                label="Marketing"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("marketing-tab")}
                isSelected={currentPathname.includes("marketing-tab")}
              />
              <PolicyTabCard
                label="Policy Changes/Upload"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("policy-change-request-list")}
                isSelected={currentPathname.includes(
                  "policy-change-request-list",
                )}
              />
              <PolicyTabCard
                label="Policy Forms"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("policy-forms-tab")}
                isSelected={currentPathname.includes("policy-forms-tab")}
              />
              <PolicyTabCard
                label="G4 Sign"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("g4-sign")}
                isSelected={currentPathname.includes("g4-sign")}
              />
              <PolicyTabCard
                label="Schedules"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("schedule-tab")}
                isSelected={currentPathname.includes("schedule-tab")}
              />

              <PolicyTabCard
                label="Questionnaires"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("questionnare-tab")}
                isSelected={currentPathname.includes("questionnare-tab")}
              />
              <PolicyTabCard
                label="Attachments"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("attachments-tab")}
                isSelected={currentPathname.includes("attachments-tab")}
              />
              <PolicyTabCard
                label="Premium Finance"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("premium-finance")}
                isSelected={currentPathname.includes("premium-finance")}
              />
              <PolicyTabCard
                label="eDocs"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("csio-edocs-list")}
                isSelected={currentPathname.includes("csio-edocs-list")}
              />

              <PolicyTabCard
                label="Contacts"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("contacts")}
                isSelected={currentPathname.includes("contacts")}
              />
              <PolicyTabCard
                label="Activities"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("all-tasks")}
                isSelected={currentPathname.includes("all-tasks")}
              />
              <PolicyTabCard
                label="Email Thread"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("email-thread")}
                isSelected={currentPathname.includes("email-thread")}
              />
              <PolicyTabCard
                label="Sequences"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("sequences")}
                isSelected={currentPathname.includes("sequences")}
              />
              <PolicyTabCard
                label="Invoices"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("invoices")}
                isSelected={currentPathname.includes("invoices")}
              />
              <PolicyTabCard
                label="Certificate of Insurance"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("certificate-of-insurance")}
                isSelected={currentPathname.includes(
                  "certificate-of-insurance",
                )}
              />
              <PolicyTabCard
                label="SOV"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("sov-tab")}
                isSelected={currentPathname.includes("sov-tab")}
              />
              <PolicyTabCard
                label="Business Interruption"
                disabled={!savedCustomerPolicyId}
                onClick={handleStep("business-interruption-tab")}
                isSelected={currentPathname.includes("business-interruption-tab")}
              />
            </Box>
          </StandadCard>
          <PageLoader
            loading={loading === LoadState.InProgress}
            error={error ? { message: error } : null}
          >
            {renderComponent()}
          </PageLoader>
        </CustomerBaiscDetailsLayout>
    </>
  );
};
